import React, { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PrinterOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { App, Button, Card, Grid, Typography, Tabs, Flex } from "antd";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import CustomerLedgerFilters from "./Components/CustomerLedgerFilters";
import CustomerLedgerTable from "./Components/CustomerLedgerTable";
// import CustomerLedgerPrintView from "./Components/CustomerLedgerPrintView";
import ReportDom from "../../../components/common/ReportDom";

const { Title } = Typography;

const fmt = (num) =>
  Number(num || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const CustomerLedger = () => {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const { message } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens    = useBreakpoint();
  const reportRef  = useRef(null);

  const {
    list,
    filters,
    linkedParty,
    closingBalance,
    supplierClosingBalance,
    supplierLedgerList,
    supplierOpeningBalance,
  } = useSelector((state) => state.customerLedger);

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: "Customer Ledger Report",
    removeAfterPrint: true,
  });

  // net balance — positive = customer owes you more than you owe them
  const netBalance = closingBalance - supplierClosingBalance;

  return (
    <div className="p-4">
      <App>

        {/* ── Header ───────────────────────────────────────────── */}
        <Card
          className="shadow-md rounded-xl mb-4"
          title={
            <div style={{ display: "flex", alignItems: "center" }}>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate("/reports")}
                style={{ marginRight: 8 }}
              />
              <Title level={4} style={{ margin: 0 }}>
                Customer Ledger
              </Title>
            </div>
          }
          extra={
            list.length > 0 && (
              <Button
                type="primary"
                icon={<PrinterOutlined />}
                onClick={handlePrint}
              >
                {!screens.xs && "Print"}
              </Button>
            )
          }
          styles={{
            header: { borderBottom: "none", padding: "12px 16px" },
            body:   { padding: 0 },
          }}
        >
          <CustomerLedgerFilters messageApi={message} />
        </Card>

        {/* ── Net balance summary — only for linked parties ─────── */}
        {linkedParty && list.length > 0 && (
          <Card
            className="shadow-md rounded-xl mb-4"
            style={{ background: "#f0f5ff", borderColor: "#adc6ff" }}
            bodyStyle={{ padding: "16px 24px" }}
          >
            <Flex gap={40} wrap="wrap" align="center">

              <div>
                <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>
                  Customer Balance
                </div>
                <div style={{ fontSize: 11, color: "#1890ff", marginBottom: 4 }}>
                  They owe you
                </div>
                <div style={{ fontWeight: 700, fontSize: 20, color: "#1890ff" }}>
                  {fmt(closingBalance)}
                </div>
              </div>

              <div style={{ fontSize: 20, color: "#ccc" }}>—</div>

              <div>
                <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>
                  Supplier Balance
                </div>
                <div style={{ fontSize: 11, color: "#ff4d4f", marginBottom: 4 }}>
                  You owe them
                </div>
                <div style={{ fontWeight: 700, fontSize: 20, color: "#ff4d4f" }}>
                  {fmt(supplierClosingBalance)}
                </div>
              </div>

              <div style={{ fontSize: 20, color: "#ccc" }}>=</div>

              <div>
                <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>
                  Net Balance
                </div>
                <div style={{
                  fontSize: 11,
                  color: netBalance >= 0 ? "#52c41a" : "#ff4d4f",
                  marginBottom: 4,
                }}>
                  {netBalance >= 0 ? "In your favor" : "You owe them"}
                </div>
                <div style={{
                  fontWeight: 700,
                  fontSize: 20,
                  color: netBalance >= 0 ? "#52c41a" : "#ff4d4f",
                }}>
                  {fmt(Math.abs(netBalance))}
                </div>
              </div>

              <div style={{ marginLeft: "auto", fontSize: 12, color: "#888" }}>
                Supplier A/C: <strong>{linkedParty.linked_acc_code}</strong>
              </div>

            </Flex>
          </Card>
        )}

        {/* ── Ledger — tabs if linked, single card if not ────────── */}
        {linkedParty && list.length > 0 ? (
          <Card className="shadow-md rounded-xl">
            <Tabs
              defaultActiveKey="customer"
              size="middle"
              items={[
                {
                  key:   "customer",
                  label: (
                    <span>
                      Customer Ledger
                      <span style={{
                        marginLeft: 8,
                        fontSize: 12,
                        color: "#1890ff",
                        fontWeight: 600,
                      }}>
                        {fmt(closingBalance)}
                      </span>
                    </span>
                  ),
                  children: <CustomerLedgerTable />,
                },
                {
                  key:   "supplier",
                  label: (
                    <span>
                      Supplier Ledger
                      <span style={{
                        marginLeft: 8,
                        fontSize: 12,
                        color: "#ff4d4f",
                        fontWeight: 600,
                      }}>
                        {fmt(supplierClosingBalance)}
                      </span>
                    </span>
                  ),
                  children: (
                    <CustomerLedgerTable
                      isSupplierTab
                      supplierList={supplierLedgerList}
                      supplierOpening={supplierOpeningBalance}
                      supplierClosing={supplierClosingBalance}
                    />
                  ),
                },
              ]}
            />
          </Card>
        ) : (
          <Card className="shadow-md rounded-xl">
            <CustomerLedgerTable />
          </Card>
        )}

      </App>

      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title="Customer Ledger Report"
          mode="no-title"
          filters={filters}
          renderTable={() => <CustomerLedgerTable printMode />}
          footer
        />
      </div>
    </div>
  );
};

export default CustomerLedger;